import { supabase } from '../../shared/supabase/supabaseClient';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { useBusinessProfileStore } from '../../shell/session/store/useBusinessProfileStore';
import { orgCanFetchHospitalitySnapshot } from '../domain/hospitalitySnapshotAccess';
import type {
  DiningTable,
  DiningTableStatus,
  GuestSignal,
  KitchenPrintQueueHealth,
  KitchenTicket,
  KitchenTicketLine,
  KitchenTicketStatus,
  ProductStation,
  Reservation,
  ReservationStatus,
  StaffTableDraftPayload,
  StaffVisitSale,
  StaffVisitSaleLine,
  Station,
  TableDraftSummary,
  TableOrderDraft,
  TableOrderDraftLine,
  TableSession,
} from '../domain/hospitalityTypes';

function throwIfError(error: { message: string } | null): void {
  if (error) throw new Error(error.message);
}

function throwHospitalityWriteFailure(
  op: string,
  error: unknown,
  itemId?: string,
  expected = false,
): never {
  const err = error instanceof Error ? error : new Error(String(error));
  captureOpsFailure({
    module: 'hospitality',
    op,
    error: err,
    expected,
    ingestError: err.message,
    itemId,
  });
  throw err;
}

function throwIfWriteError(
  op: string,
  error: { message: string } | null,
  itemId?: string,
): void {
  if (!error) return;
  throwHospitalityWriteFailure(op, new Error(error.message), itemId);
}

type RemoteDiningTableRow = {
  id: string;
  branch_id: string;
  name: string;
  capacity?: number | null;
  status?: string | null;
  sort_order?: number | null;
  occupied_at?: string | null;
  assigned_user_id?: string | null;
  pos_x?: number | null;
  pos_y?: number | null;
  updated_at?: string | null;
  public_token?: string | null;
};

type RemoteKitchenTicketRow = {
  id: string;
  branch_id: string;
  sale_id?: string | null;
  dining_table_id?: string | null;
  customer_order_id?: string | null;
  status?: string | null;
  hold?: boolean | null;
  is_all_day?: boolean | null;
  rush?: boolean | null;
  fulfillment_type?: string | null;
  notes?: string | null;
  lines?: KitchenTicketLine[] | null;
  created_at?: string | null;
  status_entered_at?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  sla_deadline_at?: string | null;
  payment_state?: string | null;
};

const mapDiningTable = (row: RemoteDiningTableRow): DiningTable => ({
  id: String(row.id),
  branchId: String(row.branch_id),
  name: String(row.name ?? ''),
  capacity: Math.max(1, Number(row.capacity) || 4),
  status: (row.status as DiningTableStatus) ?? 'available',
  sortOrder: Number(row.sort_order) || 0,
  occupiedAt: row.occupied_at ?? null,
  assignedUserId: row.assigned_user_id ?? null,
  posX: row.pos_x ?? null,
  posY: row.pos_y ?? null,
  updatedAt: row.updated_at ?? null,
  publicToken: row.public_token ?? null,
});

const mapKitchenTicketLine = (raw: any): KitchenTicketLine => {
  const line: KitchenTicketLine = {
    name: String(raw.name ?? 'Ítem'),
    qty: Number(raw.qty) || 1,
  };
  const optionsLabel = raw.optionsLabel ?? raw.options_label;
  if (optionsLabel != null) line.optionsLabel = optionsLabel;
  const lineNote = raw.lineNote ?? raw.line_note;
  if (lineNote != null) line.lineNote = lineNote;
  const productId = raw.productId ?? raw.product_id;
  if (productId != null) line.productId = productId;
  const status = raw.status ?? raw.line_status;
  if (status != null) line.status = status as KitchenTicketLine['status'];
  const comboName = raw.comboName ?? raw.combo_name;
  if (comboName != null) line.comboName = String(comboName);
  const menuOptions = raw.menuOptions ?? raw.menu_options;
  if (Array.isArray(menuOptions)) {
    line.menuOptions = menuOptions
      .map((option: any) => ({
        groupId: String(option.groupId ?? option.group_id ?? ''),
        groupName: String(option.groupName ?? option.group_name ?? ''),
        choiceId: String(option.choiceId ?? option.choice_id ?? ''),
        choiceName: String(option.choiceName ?? option.choice_name ?? ''),
        priceDelta: Number(option.priceDelta ?? option.price_delta ?? 0) || 0,
        qty: Math.max(1, Number(option.qty) || 1),
        linkedProductId: option.linkedProductId ?? option.linked_product_id ?? null,
        modifierKind: option.modifierKind ?? option.modifier_kind ?? null,
        allergenTags: Array.isArray(option.allergenTags ?? option.allergen_tags)
          ? (option.allergenTags ?? option.allergen_tags).filter(Boolean)
          : [],
        kitchenLabel: option.kitchenLabel ?? option.kitchen_label ?? null,
        substitutesChoiceId: option.substitutesChoiceId ?? option.substitutes_choice_id ?? null,
      }))
      .filter((option: any) => option.choiceName);
  }
  return line;
};

const mapKitchenTicket = (row: RemoteKitchenTicketRow): KitchenTicket => ({
  id: String(row.id),
  branchId: String(row.branch_id),
  saleId: row.sale_id ?? null,
  diningTableId: row.dining_table_id ?? null,
  customerOrderId: row.customer_order_id ?? null,
  status: (row.status as KitchenTicketStatus) ?? 'pending',
  hold: row.hold ?? false,
  isAllDay: row.is_all_day ?? false,
  rush: row.rush ?? false,
  fulfillmentType: row.fulfillment_type ?? null,
  notes: row.notes ?? null,
  lines: Array.isArray(row.lines) ? row.lines.map(mapKitchenTicketLine) : [],
  createdAt: row.created_at ?? null,
  statusEnteredAt: row.status_entered_at ?? null,
  customerName: row.customer_name?.trim() || null,
  customerPhone: row.customer_phone?.trim() || null,
  slaDeadlineAt: row.sla_deadline_at ?? null,
  paymentState:
    row.payment_state === 'pending' || row.payment_state === 'paid'
      ? row.payment_state
      : 'not_required',
});

type RemoteStationRow = {
  id: string;
  branch_id: string;
  name: string;
  sort_order?: number | null;
};

type RemoteProductStationRow = {
  product_id: string;
  station_id: string;
};

const mapStation = (row: RemoteStationRow): Station => ({
  id: String(row.id),
  branchId: String(row.branch_id),
  name: String(row.name ?? ''),
  sortOrder: Number(row.sort_order) || 0,
});

const mapProductStation = (row: RemoteProductStationRow): ProductStation => ({
  productId: String(row.product_id),
  stationId: String(row.station_id),
});

type RemoteTableSessionRow = {
  id: string;
  dining_table_id: string;
  branch_id: string;
  covers?: number | null;
  opened_at?: string | null;
  cart_json?: unknown[] | null;
  promo_json?: unknown | null;
  tip_intent?: unknown | null;
  assigned_user_id?: string | null;
  payment_status?: string | null;
  line_count?: number | null;
  created?: boolean | null;
};

const mapTableSession = (row: RemoteTableSessionRow): TableSession => {
  let cartJson: unknown[] | undefined;
  if (Array.isArray(row.cart_json)) {
    cartJson = row.cart_json;
  } else if (typeof row.cart_json === 'string') {
    try {
      const parsed = JSON.parse(row.cart_json) as unknown;
      cartJson = Array.isArray(parsed) ? parsed : undefined;
    } catch {
      cartJson = undefined;
    }
  }
  let tipIntent: TableSession['tipIntent'] = null;
  if (row.tip_intent && typeof row.tip_intent === 'object' && !Array.isArray(row.tip_intent)) {
    const tip = row.tip_intent as Record<string, unknown>;
    const amount = Math.max(0, Math.round(Number(tip.amount ?? 0) * 100) / 100);
    const percentRaw = tip.percent;
    const percent =
      percentRaw == null || percentRaw === ''
        ? null
        : Number.isFinite(Number(percentRaw))
          ? Number(percentRaw)
          : null;
    const source = String(tip.source ?? 'staff').toLowerCase() === 'guest' ? 'guest' : 'staff';
    tipIntent = { amount, percent, source };
  }
  return {
    id: String(row.id),
    diningTableId: String(row.dining_table_id),
    branchId: String(row.branch_id),
    covers: Math.max(1, Number(row.covers) || 2),
    openedAt: row.opened_at ?? null,
    cartJson,
    promoJson: row.promo_json ?? null,
    tipIntent,
    assignedUserId: row.assigned_user_id != null ? String(row.assigned_user_id) : null,
    paymentStatus: row.payment_status != null ? String(row.payment_status) : null,
    lineCount: row.line_count != null ? Number(row.line_count) : undefined,
    created: row.created ?? undefined,
  };
};

type RemotePrintQueueHealthRow = {
  pending_count?: number | null;
  failed_count?: number | null;
  stuck_count?: number | null;
  oldest_queued_at?: string | null;
  stuck_after_minutes?: number | null;
};

const mapPrintQueueHealth = (
  row: RemotePrintQueueHealthRow | null | undefined,
): KitchenPrintQueueHealth | null => {
  if (!row || typeof row !== 'object') return null;
  return {
    pendingCount: Math.max(0, Number(row.pending_count) || 0),
    failedCount: Math.max(0, Number(row.failed_count) || 0),
    stuckCount: Math.max(0, Number(row.stuck_count) || 0),
    oldestQueuedAt: row.oldest_queued_at ?? null,
    stuckAfterMinutes: Math.max(1, Number(row.stuck_after_minutes) || 5),
  };
};

export type HospitalitySnapshot = {
  diningTables: DiningTable[];
  kitchenTickets: KitchenTicket[];
  stations: Station[];
  productStations: ProductStation[];
  tableSessions: TableSession[];
  tableDrafts: TableDraftSummary[];
  /** null si el backend todavía no emite print_queue (compat). */
  printQueue: KitchenPrintQueueHealth | null;
};

const EMPTY_HOSPITALITY_SNAPSHOT: HospitalitySnapshot = {
  diningTables: [],
  kitchenTickets: [],
  stations: [],
  productStations: [],
  tableSessions: [],
  tableDrafts: [],
  printQueue: null,
};

type RemoteTableDraftSummaryRow = {
  dining_table_id: string;
  branch_id: string;
  draft_id?: string | null;
  draft_line_count?: number | null;
  draft_updated_at?: string | null;
  open_signal_count?: number | null;
  has_ready_to_order?: boolean | null;
  has_call_waiter?: boolean | null;
  has_request_bill?: boolean | null;
  looking_guest_count?: number | null;
};

const mapTableDraftSummary = (row: RemoteTableDraftSummaryRow): TableDraftSummary => ({
  diningTableId: String(row.dining_table_id),
  branchId: String(row.branch_id),
  draftId: row.draft_id ? String(row.draft_id) : null,
  draftLineCount: Math.max(0, Number(row.draft_line_count) || 0),
  draftUpdatedAt: row.draft_updated_at ?? null,
  openSignalCount: Math.max(0, Number(row.open_signal_count) || 0),
  hasReadyToOrder: Boolean(row.has_ready_to_order),
  hasCallWaiter: Boolean(row.has_call_waiter),
  hasRequestBill: Boolean(row.has_request_bill),
  lookingGuestCount: Math.max(0, Number(row.looking_guest_count) || 0),
});

const mapDraftLine = (raw: Record<string, unknown>): TableOrderDraftLine => ({
  id: String(raw.id ?? ''),
  clientLineId: String(raw.client_line_id ?? ''),
  productId: raw.product_id != null ? String(raw.product_id) : null,
  name: String(raw.name ?? 'Ítem'),
  qty: Number(raw.qty) || 1,
  unitPrice: Number(raw.unit_price) || 0,
  unitNote: raw.unit_note != null ? String(raw.unit_note) : null,
  optionsLabel: raw.options_label != null ? String(raw.options_label) : null,
  optionsJson: Array.isArray(raw.options_json) ? raw.options_json : [],
  addedByRole: String(raw.added_by_role ?? 'guest'),
  addedByDeviceId: raw.added_by_device_id != null ? String(raw.added_by_device_id) : null,
});

const mapDraft = (raw: Record<string, unknown> | null | undefined): TableOrderDraft | null => {
  if (!raw || typeof raw !== 'object') return null;
  const linesRaw = Array.isArray(raw.lines) ? raw.lines : [];
  return {
    id: String(raw.id ?? ''),
    diningTableId: String(raw.dining_table_id ?? ''),
    tableSessionId: raw.table_session_id != null ? String(raw.table_session_id) : null,
    status: String(raw.status ?? 'active'),
    version: Number(raw.version) || 1,
    updatedAt: String(raw.updated_at ?? ''),
    lastActivityAt: String(raw.last_activity_at ?? ''),
    lines: linesRaw.map((l) => mapDraftLine(l as Record<string, unknown>)),
  };
};

const snapshotCache = new Map<string, HospitalitySnapshot>();
const snapshotInFlight = new Map<string, Promise<HospitalitySnapshot>>();

/** Expuesto para tests. No usar en producción. */
export function resetHospitalitySnapshotCache(): void {
  snapshotCache.clear();
  snapshotInFlight.clear();
}

async function fetchRemoteHospitalitySnapshot(
  branchId: string | null,
  recentWindowMinutes?: number,
): Promise<HospitalitySnapshot> {
  const args: Record<string, unknown> = { p_branch_id: branchId };
  if (recentWindowMinutes != null) args.p_recent_window_minutes = recentWindowMinutes;
  const { data, error } = await supabase.rpc('get_hospitality_snapshot', args as any);
  throwIfError(error);
  const payload = (data ?? {}) as {
    dining_tables?: RemoteDiningTableRow[];
    kitchen_tickets?: RemoteKitchenTicketRow[];
    stations?: RemoteStationRow[];
    product_stations?: RemoteProductStationRow[];
    table_sessions?: RemoteTableSessionRow[];
    table_drafts?: RemoteTableDraftSummaryRow[];
    print_queue?: RemotePrintQueueHealthRow | null;
  };
  const tableRows = Array.isArray(payload.dining_tables) ? payload.dining_tables : [];
  const ticketRows = Array.isArray(payload.kitchen_tickets) ? payload.kitchen_tickets : [];
  const stationRows = Array.isArray(payload.stations) ? payload.stations : [];
  const productStationRows = Array.isArray(payload.product_stations) ? payload.product_stations : [];
  const sessionRows = Array.isArray(payload.table_sessions) ? payload.table_sessions : [];
  const draftRows = Array.isArray(payload.table_drafts) ? payload.table_drafts : [];
  return {
    diningTables: tableRows.map(mapDiningTable),
    kitchenTickets: ticketRows.map(mapKitchenTicket),
    stations: stationRows.map(mapStation),
    productStations: productStationRows.map(mapProductStation),
    tableSessions: sessionRows.map(mapTableSession),
    tableDrafts: draftRows.map(mapTableDraftSummary),
    printQueue: mapPrintQueueHealth(payload.print_queue),
  };
}

export async function fetchHospitalitySnapshot(
  branchId: string | null,
  recentWindowMinutes?: number,
): Promise<HospitalitySnapshot> {
  const profile = useBusinessProfileStore.getState().profile;
  if (!orgCanFetchHospitalitySnapshot(profile)) {
    return EMPTY_HOSPITALITY_SNAPSHOT;
  }
  const cacheKey = branchId ?? '_';
  const cached = snapshotCache.get(cacheKey);
  // Dedupe de llamadas en vuelo: los providers del shell (kitchen tickets,
  // ready alerts, guest signals) disparan el snapshot en el mismo tick.
  // `undefined` usa el default del RPC (30 min) → misma key que `30`.
  const inFlightKey = `${cacheKey}|${recentWindowMinutes ?? 30}`;
  const existing = snapshotInFlight.get(inFlightKey);
  if (existing) return existing;
  const promise = (async () => {
    try {
      const fresh = await fetchRemoteHospitalitySnapshot(branchId, recentWindowMinutes);
      snapshotCache.set(cacheKey, fresh);
      return fresh;
    } catch (error) {
      if (cached) return cached;
      throw error;
    }
  })().finally(() => {
    snapshotInFlight.delete(inFlightKey);
  });
  snapshotInFlight.set(inFlightKey, promise);
  return promise;
}

export async function upsertDiningTable(args: {
  branchId: string;
  name: string;
  capacity?: number;
  tableId?: string | null;
}): Promise<string> {
  const { data, error } = await supabase.rpc('upsert_dining_table', {
    p_branch_id: args.branchId,
    p_name: args.name.trim(),
    p_capacity: args.capacity ?? 4,
    p_table_id: args.tableId ?? null,
  });
  throwIfWriteError('hospitality.table_layout', error, args.tableId ?? args.branchId);
  const id = (data as { id?: string } | null)?.id;
  if (!id) {
    throwHospitalityWriteFailure(
      'hospitality.table_layout',
      new Error('No se recibió el id de la mesa'),
      args.tableId ?? args.branchId,
    );
  }
  return String(id);
}

export async function updateDiningTableStatus(args: {
  tableId: string;
  status: DiningTableStatus;
  branchId: string;
}): Promise<void> {
  const { error } = await supabase.rpc('update_dining_table_status', {
    p_table_id: args.tableId,
    p_status: args.status,
    p_branch_id: args.branchId,
  });
  throwIfWriteError('hospitality.table_layout', error, args.tableId);
}

export async function deleteDiningTable(args: {
  tableId: string;
  branchId: string;
}): Promise<void> {
  const { error } = await supabase.rpc('delete_dining_table', {
    p_table_id: args.tableId,
    p_branch_id: args.branchId,
  });
  throwIfWriteError('hospitality.table_layout', error, args.tableId);
}

export async function updateKitchenTicketStatus(
  ticketId: string,
  status: KitchenTicketStatus,
): Promise<void> {
  const { error } = await supabase.rpc('update_kitchen_ticket_status', {
    p_ticket_id: ticketId,
    p_status: status,
  });
  if (error) {
    const msg = error.message ?? '';
    if (msg.includes('order_cancelled_cannot_restore_ticket')) {
      throw new Error('El pedido online está cancelado; no se puede restaurar la comanda.');
    }
    if (msg.includes('payment_pending')) {
      throw new Error('Esta comanda todavía no está cobrada. Cobrala en el POS antes de entregarla.');
    }
    if (msg.includes('invalid_status_transition')) {
      throw new Error('Ese paso no aplica a esta comanda. Usá el botón del estado siguiente.');
    }
    throwHospitalityWriteFailure(
      'hospitality.ticket_status',
      new Error(msg || 'No se pudo actualizar la comanda'),
      ticketId,
      /permission denied|not authenticated|module_permission/i.test(msg),
    );
  }
}

/** Contrato público KDS-S7: avanzar o restaurar el estado de una comanda. */
export async function bumpKitchenTicket(
  ticketId: string,
  status: KitchenTicketStatus,
): Promise<void> {
  return updateKitchenTicketStatus(ticketId, status);
}

export async function bumpKitchenTicketLine(
  ticketId: string,
  productId: string,
): Promise<{ allDone: boolean }> {
  const { data, error } = await supabase.rpc('bump_kitchen_ticket_line', {
    p_ticket_id: ticketId,
    p_product_id: productId,
  });
  if (error) {
    throw new Error(error.message || 'No se pudo marcar la línea');
  }
  const row = data as { all_done?: boolean } | null;
  return { allDone: row?.all_done === true };
}

export async function createKitchenTicket(args: {
  branchId: string;
  saleId?: string | null;
  diningTableId?: string | null;
  notes?: string | null;
  lines?: KitchenTicketLine[] | null;
  /** 'pending' solo aplica a comandas de mostrador disparadas antes de cobrar (sin venta ni mesa). */
  paymentState?: 'pending';
}): Promise<string | null> {
  const { data, error } = await supabase.rpc('create_kitchen_ticket', {
    p_branch_id: args.branchId,
    p_sale_id: args.saleId ?? null,
    p_dining_table_id: args.diningTableId ?? null,
    p_notes: args.notes ?? null,
    p_lines: args.lines ?? [],
    p_payment_state: args.paymentState ?? 'not_required',
  });
  throwIfWriteError('hospitality.ticket_create', error, args.saleId ?? undefined);
  const id = (data as { id?: string | null } | null)?.id;
  if (id === undefined) {
    throwHospitalityWriteFailure(
      'hospitality.ticket_create',
      new Error('No se recibió respuesta de la comanda'),
      args.saleId ?? undefined,
    );
  }
  return id ?? null;
}

export async function markProductUnavailable(
  productId: string,
  branchId: string,
): Promise<void> {
  const { error } = await supabase.rpc('mark_product_unavailable_from_kitchen', {
    p_product_id: productId,
    p_branch_id: branchId,
  });
  if (error) {
    throw new Error(error.message || 'No se pudo marcar el producto como no disponible');
  }
}

export async function tableHasReadyTickets(tableId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('table_has_ready_tickets', {
    p_table_id: tableId,
  });
  throwIfError(error);
  return (data as { has_ready?: boolean } | null)?.has_ready ?? false;
}

export async function openTableSession(args: {
  branchId: string;
  diningTableId: string;
  covers?: number;
}): Promise<TableSession> {
  const { data, error } = await supabase.rpc('open_table_session', {
    p_branch_id: args.branchId,
    p_dining_table_id: args.diningTableId,
    p_covers: args.covers ?? 2,
  });
  throwIfWriteError('hospitality.session_open', error, args.diningTableId);
  if (!data || typeof data !== 'object') {
    throwHospitalityWriteFailure(
      'hospitality.session_open',
      new Error('No se pudo abrir la mesa'),
      args.diningTableId,
    );
  }
  return mapTableSession(data as RemoteTableSessionRow);
}

/** Edita comensales reales de la sesión abierta (≠ capacity de la mesa). */
export async function updateTableSessionCovers(
  sessionId: string,
  covers: number,
): Promise<TableSession> {
  const { data, error } = await supabase.rpc('update_table_session_covers', {
    p_session_id: sessionId,
    p_covers: Math.max(1, Math.min(99, Math.round(covers) || 1)),
  });
  throwIfWriteError('hospitality.session_covers', error, sessionId);
  if (!data || typeof data !== 'object') {
    throwHospitalityWriteFailure(
      'hospitality.session_covers',
      new Error('No se pudo actualizar cubiertos'),
      sessionId,
    );
  }
  return mapTableSession(data as RemoteTableSessionRow);
}

export async function getOpenTableSession(diningTableId: string): Promise<TableSession | null> {
  const { data, error } = await supabase.rpc('get_open_table_session', {
    p_dining_table_id: diningTableId,
  });
  throwIfError(error);
  if (!data) return null;
  return mapTableSession(data as RemoteTableSessionRow);
}

export async function upsertTableSessionCart(args: {
  sessionId: string;
  cartJson: unknown[];
  promoJson?: unknown | null;
}): Promise<void> {
  const { error } = await supabase.rpc('upsert_table_session_cart', {
    p_session_id: args.sessionId,
    p_cart_json: args.cartJson,
    p_promo_json: args.promoJson ?? null,
  });
  throwIfWriteError('hospitality.session_cart', error, args.sessionId);
}

export async function upsertTableSessionTipIntent(args: {
  sessionId: string;
  tipIntent: { amount: number; percent: number | null; source: 'guest' | 'staff' } | null;
}): Promise<void> {
  const { error } = await supabase.rpc('upsert_table_session_tip_intent', {
    p_session_id: args.sessionId,
    p_tip_intent: args.tipIntent,
  });
  throwIfWriteError('hospitality.session_tip_intent', error, args.sessionId);
}

export async function closeTableSession(args: {
  sessionId: string;
  saleId?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('close_table_session', {
    p_session_id: args.sessionId,
    p_sale_id: args.saleId ?? null,
  });
  throwIfWriteError('hospitality.session_close', error, args.sessionId);
}

export async function transferTableSession(
  sourceTableId: string,
  targetTableId: string,
): Promise<void> {
  const { error } = await supabase.rpc('transfer_table_session', {
    p_source_table_id: sourceTableId,
    p_target_table_id: targetTableId,
  });
  throwIfWriteError('hospitality.session_transfer', error, sourceTableId);
}

export async function syncTableSessionKitchenSentFromOpenTickets(
  diningTableId: string,
): Promise<number> {
  const { data, error } = await supabase.rpc('sync_table_session_kitchen_sent_from_open_tickets', {
    p_dining_table_id: diningTableId,
  });
  throwIfError(error);
  return Math.max(0, Number((data as { marked_line_count?: number } | null)?.marked_line_count ?? 0));
}

export async function createKitchenTicketForTable(args: {
  branchId: string;
  diningTableId: string;
  notes?: string | null;
  lines: KitchenTicketLine[];
}): Promise<string | null> {
  const { data, error } = await supabase.rpc('create_kitchen_ticket_for_table', {
    p_branch_id: args.branchId,
    p_dining_table_id: args.diningTableId,
    p_notes: args.notes ?? null,
    p_lines: args.lines,
  });
  throwIfWriteError('hospitality.ticket_create', error, args.diningTableId);
  const id = (data as { id?: string | null } | null)?.id;
  if (id === undefined) {
    throwHospitalityWriteFailure(
      'hospitality.ticket_create',
      new Error('No se recibió respuesta de la comanda'),
      args.diningTableId,
    );
  }
  if (id) {
    await syncTableSessionKitchenSentFromOpenTickets(args.diningTableId).catch(() => {
      /* RPC post-migration; create_kitchen_ticket_for_table ya marca en server */
    });
  }
  return id ?? null;
}

export async function staffGetTableDraft(
  branchId: string,
  diningTableId: string,
): Promise<StaffTableDraftPayload> {
  const { data, error } = await supabase.rpc('staff_get_table_draft', {
    p_branch_id: branchId,
    p_dining_table_id: diningTableId,
  });
  throwIfError(error);
  const payload = (data ?? {}) as {
    draft?: Record<string, unknown> | null;
    open_signals?: Array<Record<string, unknown>>;
    presence?: Array<Record<string, unknown>>;
    account?: Record<string, unknown> | null;
    visit_sales?: unknown;
  };
  const signals = Array.isArray(payload.open_signals) ? payload.open_signals : [];
  const presence = Array.isArray(payload.presence) ? payload.presence : [];
  const accountRaw = payload.account && typeof payload.account === 'object' ? payload.account : null;
  let cartJson: unknown[] = [];
  if (accountRaw) {
    const rawCart = accountRaw.cart_json;
    if (Array.isArray(rawCart)) cartJson = rawCart;
    else if (typeof rawCart === 'string') {
      try {
        const parsed = JSON.parse(rawCart) as unknown;
        cartJson = Array.isArray(parsed) ? parsed : [];
      } catch {
        cartJson = [];
      }
    }
  }
  const visitSalesRaw = Array.isArray(payload.visit_sales) ? payload.visit_sales : [];
  const visitSales: StaffVisitSale[] = visitSalesRaw
    .map((raw): StaffVisitSale | null => {
      const row = (raw ?? {}) as Record<string, unknown>;
      const saleId = row.sale_id != null ? String(row.sale_id) : '';
      if (!saleId) return null;
      const linesRaw = Array.isArray(row.lines) ? row.lines : [];
      const lines: StaffVisitSaleLine[] = linesRaw.map((l) => {
        const line = (l ?? {}) as Record<string, unknown>;
        return {
          name: String(line.name ?? 'Ítem'),
          qty: Math.max(1, Number(line.qty) || 1),
          price: Number(line.price) || 0,
          lineNote: line.lineNote != null ? String(line.lineNote) : null,
          optionsLabel: line.optionsLabel != null ? String(line.optionsLabel) : null,
        };
      });
      return {
        sessionId: row.session_id != null ? String(row.session_id) : saleId,
        saleId,
        closedAt: row.closed_at != null ? String(row.closed_at) : null,
        total: Number(row.total) || lines.reduce((s, l) => s + l.price * l.qty, 0),
        lineCount: Math.max(lines.length, Number(row.line_count) || 0),
        lines,
      };
    })
    .filter((x): x is StaffVisitSale => x != null);

  return {
    draft: mapDraft(payload.draft),
    openSignals: signals.map(
      (s): GuestSignal => ({
        id: String(s.id ?? ''),
        kind: String(s.kind ?? ''),
        status: String(s.status ?? 'open'),
        createdAt: String(s.created_at ?? ''),
        guestDeviceId: s.guest_device_id != null ? String(s.guest_device_id) : null,
      }),
    ),
    presence: presence.map((p) => ({
      guestDeviceId: String(p.guest_device_id ?? ''),
      lastSeenAt: String(p.last_seen_at ?? ''),
    })),
    visitSales,
    account: accountRaw
      ? {
          tableStatus: String(accountRaw.table_status ?? 'available'),
          needsLiberate: Boolean(accountRaw.needs_liberate),
          occupiedAt: accountRaw.occupied_at != null ? String(accountRaw.occupied_at) : null,
          assignedUserId: accountRaw.assigned_user_id != null ? String(accountRaw.assigned_user_id) : null,
          sessionId: accountRaw.session_id != null ? String(accountRaw.session_id) : null,
          sessionStatus: accountRaw.session_status != null ? String(accountRaw.session_status) : null,
          openedAt: accountRaw.opened_at != null ? String(accountRaw.opened_at) : null,
          closedAt: accountRaw.closed_at != null ? String(accountRaw.closed_at) : null,
          saleId: accountRaw.sale_id != null ? String(accountRaw.sale_id) : null,
          covers: Math.max(1, Number(accountRaw.covers) || 1),
          cartJson,
          openKitchenCount: Math.max(0, Number(accountRaw.open_kitchen_count) || 0),
          readyKitchenCount: Math.max(0, Number(accountRaw.ready_kitchen_count) || 0),
          kitchenReadyLines: Array.isArray(accountRaw.kitchen_ready_lines)
            ? (accountRaw.kitchen_ready_lines as unknown[])
                .map((raw) => {
                  const row = (raw ?? {}) as Record<string, unknown>;
                  return {
                    name: String(row.name ?? 'Ítem'),
                    qty: Math.max(1, Number(row.qty) || 1),
                  };
                })
                .filter((l) => l.name.length > 0)
            : [],
          tipIntent: (() => {
            const raw = accountRaw.tip_intent;
            if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
            const tip = raw as Record<string, unknown>;
            const amount = Math.max(0, Number(tip.amount) || 0);
            if (amount <= 0) return null;
            const percentRaw = tip.percent;
            const percent =
              percentRaw == null || percentRaw === ''
                ? null
                : Number.isFinite(Number(percentRaw))
                  ? Number(percentRaw)
                  : null;
            const sourceRaw = String(tip.source ?? 'guest');
            const source = sourceRaw === 'staff' ? 'staff' : 'guest';
            return { amount, percent, source };
          })(),
        }
      : null,
  };
}

export async function staffAckGuestSignal(branchId: string, signalId: string): Promise<void> {
  const { error } = await supabase.rpc('staff_ack_guest_signal', {
    p_branch_id: branchId,
    p_signal_id: signalId,
  });
  throwIfWriteError('hospitality.guest_signal_ack', error, signalId);
}

export async function staffAckTableGuestSignals(
  branchId: string,
  diningTableId: string,
): Promise<number> {
  const { data, error } = await supabase.rpc('staff_ack_table_guest_signals', {
    p_branch_id: branchId,
    p_dining_table_id: diningTableId,
  });
  throwIfWriteError('hospitality.guest_signals_ack_table', error, diningTableId);
  const payload = (data ?? {}) as { acked?: number };
  return Math.max(0, Number(payload.acked) || 0);
}

export async function staffApplyTableDraftToSession(args: {
  branchId: string;
  diningTableId: string;
  covers?: number;
  notes?: string | null;
  /** true = comanda a cocina + cuenta; false = solo volcar a cart_json (Ir a cobrar). */
  sendToKitchen?: boolean;
}): Promise<{
  kitchenTicketId: string | null;
  sessionId: string | null;
  mergedLineCount: number;
  cartLineCount: number;
  sentToKitchen: boolean;
  cartJson: unknown[];
  draft: TableOrderDraft | null;
}> {
  const { data, error } = await supabase.rpc('staff_apply_table_draft_to_session', {
    p_branch_id: args.branchId,
    p_dining_table_id: args.diningTableId,
    p_covers: args.covers ?? 2,
    p_notes: args.notes ?? null,
    p_send_to_kitchen: args.sendToKitchen ?? false,
  });
  throwIfWriteError('hospitality.draft_apply_session', error, args.diningTableId);
  const payload = (data ?? {}) as {
    kitchen_ticket_id?: string | null;
    session_id?: string | null;
    merged_line_count?: number | null;
    cart_line_count?: number | null;
    sent_to_kitchen?: boolean | null;
    cart_json?: unknown;
    draft?: Record<string, unknown> | null;
  };
  const cartJson = Array.isArray(payload.cart_json)
    ? payload.cart_json
    : typeof payload.cart_json === 'string'
      ? (() => {
          try {
            const parsed = JSON.parse(payload.cart_json) as unknown;
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        })()
      : [];
  if (Boolean(payload.sent_to_kitchen)) {
    await syncTableSessionKitchenSentFromOpenTickets(args.diningTableId).catch(() => {});
  }
  return {
    kitchenTicketId: payload.kitchen_ticket_id ? String(payload.kitchen_ticket_id) : null,
    sessionId: payload.session_id ? String(payload.session_id) : null,
    mergedLineCount: Number(payload.merged_line_count ?? 0),
    cartLineCount: Number(payload.cart_line_count ?? cartJson.length),
    sentToKitchen: Boolean(payload.sent_to_kitchen),
    cartJson,
    draft: mapDraft(payload.draft),
  };
}

export async function staffConfirmTableDraftRound(args: {
  branchId: string;
  diningTableId: string;
  covers?: number;
  notes?: string | null;
}): Promise<{ kitchenTicketId: string | null; draft: TableOrderDraft | null }> {
  const applied = await staffApplyTableDraftToSession({
    ...args,
    sendToKitchen: true,
  });
  return {
    kitchenTicketId: applied.kitchenTicketId,
    draft: applied.draft,
  };
}

export async function staffLiberateTableDraft(args: {
  branchId: string;
  diningTableId: string;
  force?: boolean;
}): Promise<void> {
  const { error } = await supabase.rpc('staff_liberate_table_draft', {
    p_branch_id: args.branchId,
    p_dining_table_id: args.diningTableId,
    p_force: args.force ?? false,
  });
  throwIfWriteError('hospitality.draft_liberate', error, args.diningTableId);
}

export async function staffDraftUpsertLine(args: {
  branchId: string;
  diningTableId: string;
  clientLineId: string;
  productId: string;
  name: string;
  qty: number;
  unitPrice: number;
  unitNote?: string | null;
  optionsJson?: unknown;
  optionsLabel?: string | null;
}): Promise<TableOrderDraft | null> {
  const { data, error } = await supabase.rpc('staff_draft_upsert_line', {
    p_branch_id: args.branchId,
    p_dining_table_id: args.diningTableId,
    p_client_line_id: args.clientLineId,
    p_product_id: args.productId,
    p_name: args.name,
    p_qty: args.qty,
    p_unit_price: args.unitPrice,
    p_unit_note: args.unitNote ?? null,
    p_options_json: args.optionsJson ?? [],
    p_options_label: args.optionsLabel ?? null,
  });
  throwIfWriteError('hospitality.draft_upsert_line', error, args.diningTableId);
  const payload = (data ?? {}) as { draft?: Record<string, unknown> | null };
  return mapDraft(payload.draft);
}

export async function staffDraftRemoveLine(args: {
  branchId: string;
  diningTableId: string;
  clientLineId: string;
}): Promise<TableOrderDraft | null> {
  const { data, error } = await supabase.rpc('staff_draft_remove_line', {
    p_branch_id: args.branchId,
    p_dining_table_id: args.diningTableId,
    p_client_line_id: args.clientLineId,
  });
  throwIfWriteError('hospitality.draft_remove_line', error, args.diningTableId);
  const payload = (data ?? {}) as { draft?: Record<string, unknown> | null };
  return mapDraft(payload.draft);
}

export async function staffDraftClear(branchId: string, diningTableId: string): Promise<TableOrderDraft | null> {
  const { data, error } = await supabase.rpc('staff_draft_clear', {
    p_branch_id: branchId,
    p_dining_table_id: diningTableId,
  });
  throwIfWriteError('hospitality.draft_clear', error, diningTableId);
  const payload = (data ?? {}) as { draft?: Record<string, unknown> | null };
  return mapDraft(payload.draft);
}

/** Cancela intento MP guest pendiente para poder cobrar en POS. */
export async function staffCancelTablesidePayment(sessionId: string): Promise<void> {
  const { error } = await supabase.rpc('staff_cancel_tableside_payment', {
    p_session_id: sessionId,
  });
  throwIfWriteError('hospitality.tableside_cancel_payment', error, sessionId);
}

export async function attachKitchenTicketsToSale(args: {
  saleId: string;
  tableId?: string | null;
  /** Comandas de mostrador disparadas antes de cobrar (fire-before-pay). */
  ticketIds?: string[];
}): Promise<{ attached: number }> {
  const { data, error } = await supabase.rpc('attach_kitchen_tickets_to_sale', {
    p_table_id: args.tableId ?? null,
    p_sale_id: args.saleId,
    p_ticket_ids: args.ticketIds?.length ? args.ticketIds : null,
  });
  throwIfWriteError('hospitality.ticket_attach_sale', error, args.saleId);
  return { attached: (data as { attached?: number } | null)?.attached ?? 0 };
}

export async function upsertStation(args: {
  branchId: string;
  name: string;
  sortOrder?: number;
  stationId?: string | null;
}): Promise<string> {
  const { data, error } = await supabase.rpc('upsert_station', {
    p_branch_id: args.branchId,
    p_name: args.name.trim(),
    p_sort_order: args.sortOrder ?? 0,
    p_station_id: args.stationId ?? null,
  });
  throwIfWriteError('hospitality.station', error, args.stationId ?? args.branchId);
  const id = (data as { id?: string } | null)?.id;
  if (!id) {
    throwHospitalityWriteFailure(
      'hospitality.station',
      new Error('No se recibió el id de la estación'),
      args.stationId ?? args.branchId,
    );
  }
  return String(id);
}

export async function deleteStation(stationId: string): Promise<void> {
  const { error } = await supabase.rpc('delete_station', {
    p_station_id: stationId,
  });
  throwIfWriteError('hospitality.station', error, stationId);
}

export async function setProductStations(
  productId: string,
  stationIds: string[],
  branchId: string,
): Promise<void> {
  const { error } = await supabase.rpc('set_product_stations', {
    p_product_id: productId,
    p_station_ids: stationIds,
    p_branch_id: branchId,
  });
  throwIfWriteError('hospitality.station', error, productId);
}

export async function getKitchenStationPrinters(branchId: string): Promise<Record<string, string>> {
  const { data, error } = await supabase.rpc('get_kitchen_station_printers', {
    p_branch_id: branchId,
  });
  throwIfError(error);
  if (!data || typeof data !== 'object' || Array.isArray(data)) return {};
  return Object.fromEntries(
    Object.entries(data as Record<string, unknown>)
      .filter(([, value]) => typeof value === 'string' && value.trim())
      .map(([key, value]) => [key, String(value)]),
  );
}

export async function setKitchenStationPrinter(
  branchId: string,
  stationId: string,
  printerName: string,
): Promise<void> {
  const { error } = await supabase.rpc('set_kitchen_station_printer', {
    p_branch_id: branchId,
    p_station_id: stationId,
    p_printer_name: printerName.trim(),
  });
  throwIfWriteError('hospitality.station_printer', error, stationId);
}

export async function listProductNamesForBranch(branchId: string): Promise<Array<{ id: string; name: string }>> {
  const { data: branchRow, error: branchError } = await supabase
    .from('branches')
    .select('organization_id')
    .eq('id', branchId)
    .maybeSingle();
  throwIfError(branchError);
  const organizationId = branchRow?.organization_id;
  if (!organizationId) return [];

  const { data, error } = await supabase
    .from('products')
    .select('id, name')
    .eq('organization_id', organizationId)
    .eq('is_active', true)
    .or('is_misc.is.null,is_misc.eq.false')
    .is('deleted_at', null)
    .order('name')
    .limit(500);
  throwIfError(error);
  return ((data ?? []) as Array<{ id: string; name: string | null }>).map((row) => ({
    id: String(row.id),
    name: String(row.name ?? ''),
  }));
}

export async function setKitchenTicketHold(ticketId: string, hold: boolean): Promise<void> {
  const { error } = await supabase.rpc('set_kitchen_ticket_hold', {
    p_ticket_id: ticketId,
    p_hold: hold,
  });
  throwIfWriteError('hospitality.ticket_hold', error, ticketId);
}

export async function reorderDiningTables(branchId: string, tableIds: string[]): Promise<void> {
  const { error } = await supabase.rpc('reorder_dining_tables', {
    p_branch_id: branchId,
    p_table_ids: tableIds,
  });
  throwIfWriteError('hospitality.table_layout', error, branchId);
}

export async function assignTableWaiter(tableId: string, userId: string | null): Promise<void> {
  const { error } = await supabase.rpc('assign_table_waiter', {
    p_table_id: tableId,
    p_user_id: userId,
  });
  throwIfWriteError('hospitality.table_layout', error, tableId);
}

export async function listBranchUsers(branchId: string): Promise<Array<{ id: string; name: string }>> {
  const [{ data, error }, { data: branch }] = await Promise.all([
    supabase
      .from('employee_branch_permissions')
      .select('employee_id, employees!inner(auth_user_id, full_name)')
      .eq('branch_id', branchId)
      .eq('module', 'Tables')
      .eq('can_view', true)
      .is('revoked_at', null),
    supabase.from('branches').select('organization_id').eq('id', branchId).maybeSingle(),
  ]);
  throwIfError(error);
  const users = (data ?? []).map((row: any) => {
    const emp = row.employees;
    const name = emp?.full_name ?? 'Usuario';
    return { id: String(emp?.auth_user_id ?? ''), name };
  });
  // El dueño no lleva EBP (bypass total): se agrega la row role='owner' de esta org.
  const orgId = (branch as { organization_id?: string | null } | null)?.organization_id;
  if (orgId) {
    const { data: ownerRow } = await supabase
      .from('employees')
      .select('auth_user_id, full_name')
      .eq('organization_id', orgId)
      .eq('role', 'owner')
      .eq('is_active', true)
      .maybeSingle();
    const ownerId = ownerRow?.auth_user_id ? String(ownerRow.auth_user_id) : null;
    if (ownerId && !users.some((u) => u.id === ownerId)) {
      users.unshift({ id: ownerId, name: ownerRow?.full_name?.trim() || 'Dueño' });
    }
  }
  return users;
}

export async function setDiningTablePosition(
  tableId: string,
  posX: number | null,
  posY: number | null,
): Promise<void> {
  const { error } = await supabase.rpc('set_dining_table_position', {
    p_table_id: tableId,
    p_pos_x: posX,
    p_pos_y: posY,
  });
  throwIfWriteError('hospitality.table_layout', error, tableId);
}

export async function getKitchenDefaultPrepMinutes(branchId: string): Promise<number | null> {
  const { data, error } = await supabase
    .from('public_store_profiles')
    .select('default_prep_minutes')
    .eq('branch_id', branchId)
    .maybeSingle();
  throwIfError(error);
  const minutes = Number(data?.default_prep_minutes);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
}

export type KitchenReport = {
  total: number;
  by_status: Record<string, number>;
  avg_wait_minutes: number | null;
  total_lines: number | null;
};

export type KitchenProductivityStationRow = {
  station_id: string;
  station_name: string;
  tickets: number;
  items: number;
  avg_prep_minutes: number | null;
  avg_handoff_minutes: number | null;
  p50_prep_minutes: number | null;
  p90_prep_minutes: number | null;
  p50_handoff_minutes: number | null;
  p90_handoff_minutes: number | null;
  sla_met_count: number;
  sla_missed_count: number;
};

export type KitchenProductivityReport = {
  from: string | null;
  to: string | null;
  ticket_count: number;
  item_count: number;
  avg_prep_minutes: number | null;
  avg_handoff_minutes: number | null;
  p50_prep_minutes: number | null;
  p90_prep_minutes: number | null;
  p50_handoff_minutes: number | null;
  p90_handoff_minutes: number | null;
  sla_met_count: number;
  sla_missed_count: number;
  by_station: KitchenProductivityStationRow[];
  /** Info del turno de caja cuando se filtra por `shift` (pos.cash_sessions). */
  shift: { cash_session_id: string; opened_at: string | null; closed_at: string | null } | null;
};

/** Filtros opcionales del informe (RPC 20260920292000). `shift` = id de pos.cash_sessions. */
export type KitchenProductivityFilters = {
  stationId?: string | null;
  productId?: string | null;
  shift?: string | null;
};

function parseKitchenProductivityStationRow(row: Record<string, unknown>): KitchenProductivityStationRow {
  const num = (v: unknown): number | null => (v != null && Number.isFinite(Number(v)) ? Number(v) : null);
  return {
    station_id: String(row.station_id ?? ''),
    station_name: String(row.station_name ?? 'Estación'),
    tickets: Number(row.tickets ?? 0),
    items: Number(row.items ?? 0),
    avg_prep_minutes: num(row.avg_prep_minutes),
    avg_handoff_minutes: num(row.avg_handoff_minutes),
    p50_prep_minutes: num(row.p50_prep_minutes),
    p90_prep_minutes: num(row.p90_prep_minutes),
    p50_handoff_minutes: num(row.p50_handoff_minutes),
    p90_handoff_minutes: num(row.p90_handoff_minutes),
    sla_met_count: Number(row.sla_met_count ?? 0),
    sla_missed_count: Number(row.sla_missed_count ?? 0),
  };
}

export async function getKitchenProductivityReport(
  branchId: string,
  dateFrom?: string | null,
  dateTo?: string | null,
  filters?: KitchenProductivityFilters | null,
): Promise<KitchenProductivityReport> {
  const { data, error } = await supabase.rpc('get_kitchen_productivity_report', {
    p_branch_id: branchId,
    p_date_from: dateFrom ?? null,
    p_date_to: dateTo ?? null,
    p_station_id: filters?.stationId ?? null,
    p_product_id: filters?.productId ?? null,
    p_shift: filters?.shift ?? null,
  });
  throwIfError(error);
  const r = (data ?? {}) as Partial<KitchenProductivityReport>;
  const shift = (r as { shift?: unknown }).shift;
  return {
    from: typeof r.from === 'string' ? r.from : null,
    to: typeof r.to === 'string' ? r.to : null,
    ticket_count: Number(r.ticket_count ?? 0),
    item_count: Number(r.item_count ?? 0),
    avg_prep_minutes: r.avg_prep_minutes != null ? Number(r.avg_prep_minutes) : null,
    avg_handoff_minutes: r.avg_handoff_minutes != null ? Number(r.avg_handoff_minutes) : null,
    p50_prep_minutes: r.p50_prep_minutes != null ? Number(r.p50_prep_minutes) : null,
    p90_prep_minutes: r.p90_prep_minutes != null ? Number(r.p90_prep_minutes) : null,
    p50_handoff_minutes: r.p50_handoff_minutes != null ? Number(r.p50_handoff_minutes) : null,
    p90_handoff_minutes: r.p90_handoff_minutes != null ? Number(r.p90_handoff_minutes) : null,
    sla_met_count: Number(r.sla_met_count ?? 0),
    sla_missed_count: Number(r.sla_missed_count ?? 0),
    by_station: Array.isArray(r.by_station)
      ? r.by_station.map((row) => parseKitchenProductivityStationRow(row as Record<string, unknown>))
      : [],
    shift:
      shift && typeof shift === 'object'
        ? {
            cash_session_id: String((shift as Record<string, unknown>).cash_session_id ?? ''),
            opened_at: (shift as Record<string, unknown>).opened_at != null ? String((shift as Record<string, unknown>).opened_at) : null,
            closed_at: (shift as Record<string, unknown>).closed_at != null ? String((shift as Record<string, unknown>).closed_at) : null,
          }
        : null,
  };
}

export async function getKitchenReport(
  branchId: string,
  dateFrom?: string | null,
  dateTo?: string | null,
): Promise<KitchenReport> {
  const { data, error } = await supabase.rpc('get_kitchen_report', {
    p_branch_id: branchId,
    p_date_from: dateFrom ?? null,
    p_date_to: dateTo ?? null,
  });
  throwIfError(error);
  const r = (data ?? {}) as Partial<KitchenReport>;
  return {
    total: Number(r.total ?? 0),
    by_status: r.by_status ?? {},
    avg_wait_minutes: r.avg_wait_minutes != null ? Number(r.avg_wait_minutes) : null,
    total_lines: r.total_lines != null ? Number(r.total_lines) : null,
  };
}

export type TablesideMetrics = {
  guestLines: number;
  staffLines: number;
  totalLines: number;
  guestLinesPct: number | null;
  avgSignalAckMs: number | null;
  qrBindsCount: number;
};

export async function getTablesideMetrics(
  branchId: string,
  fromIso?: string | null,
  toIso?: string | null,
): Promise<TablesideMetrics> {
  const { data, error } = await supabase.rpc('hospitality_tableside_metrics', {
    p_branch_id: branchId,
    p_from: fromIso ?? null,
    p_to: toIso ?? null,
  });
  throwIfError(error);
  const r = (data ?? {}) as Record<string, unknown>;
  return {
    guestLines: Number(r.guest_lines ?? 0),
    staffLines: Number(r.staff_lines ?? 0),
    totalLines: Number(r.total_lines ?? 0),
    guestLinesPct: r.guest_lines_pct != null ? Number(r.guest_lines_pct) : null,
    avgSignalAckMs: r.avg_signal_ack_ms != null ? Number(r.avg_signal_ack_ms) : null,
    qrBindsCount: Number(r.qr_binds_count ?? 0),
  };
}

export async function mergeDiningTables(sourceTableId: string, targetTableId: string): Promise<void> {
  const { error } = await supabase.rpc('merge_dining_tables', {
    p_source_table_id: sourceTableId,
    p_target_table_id: targetTableId,
  });
  throwIfWriteError('hospitality.table_layout', error, sourceTableId);
}

export async function splitDiningTable(
  sourceTableId: string,
  newTableName: string,
  ticketIds?: string[] | null,
): Promise<string | null> {
  const { data, error } = await supabase.rpc('split_dining_table', {
    p_source_table_id: sourceTableId,
    p_new_table_name: newTableName,
    p_ticket_ids: ticketIds ?? null,
  });
  throwIfWriteError('hospitality.table_layout', error, sourceTableId);
  const r = (data ?? {}) as { new_table_id?: string | null };
  return r.new_table_id ?? null;
}

type RemoteReservationRow = {
  id: string;
  branch_id: string;
  dining_table_id?: string | null;
  customer_name: string;
  customer_phone?: string | null;
  party_size?: number | null;
  reservation_date: string;
  reservation_time: string;
  duration_minutes?: number | null;
  status?: string | null;
  notes?: string | null;
  source?: string | null;
  created_at?: string | null;
};

const mapReservation = (row: RemoteReservationRow): Reservation => ({
  id: String(row.id),
  branchId: String(row.branch_id),
  diningTableId: row.dining_table_id ?? null,
  customerName: String(row.customer_name ?? ''),
  customerPhone: row.customer_phone ?? null,
  partySize: Number(row.party_size) || 2,
  reservationDate: String(row.reservation_date ?? ''),
  reservationTime: String(row.reservation_time ?? '').slice(0, 5),
  durationMinutes: Number(row.duration_minutes) || 90,
  status: (row.status as ReservationStatus) ?? 'confirmed',
  notes: row.notes ?? null,
  source: row.source === 'portal' ? 'portal' : 'app',
  createdAt: row.created_at ?? null,
});

export async function listReservations(
  branchId: string,
  dateFrom?: string | null,
  dateTo?: string | null,
): Promise<Reservation[]> {
  const { data, error } = await supabase.rpc('list_reservations', {
    p_branch_id: branchId,
    p_date_from: dateFrom ?? null,
    p_date_to: dateTo ?? null,
  });
  throwIfError(error);
  if (!Array.isArray(data)) return [];
  return data.map(mapReservation);
}

export async function upsertReservation(params: {
  reservationId?: string | null;
  branchId: string;
  diningTableId?: string | null;
  customerName: string;
  customerPhone?: string | null;
  partySize?: number;
  reservationDate: string;
  reservationTime: string;
  durationMinutes?: number;
  status?: ReservationStatus;
  notes?: string | null;
}): Promise<string | null> {
  const { data, error } = await supabase.rpc('upsert_reservation', {
    p_reservation_id: params.reservationId ?? null,
    p_branch_id: params.branchId,
    p_dining_table_id: params.diningTableId ?? null,
    p_customer_name: params.customerName,
    p_customer_phone: params.customerPhone ?? null,
    p_party_size: params.partySize ?? 2,
    p_reservation_date: params.reservationDate,
    p_reservation_time: params.reservationTime,
    p_duration_minutes: params.durationMinutes ?? 90,
    p_status: params.status ?? 'confirmed',
    p_notes: params.notes ?? null,
  });
  throwIfWriteError('hospitality.reservation', error, params.reservationId ?? params.branchId);
  const r = (data ?? {}) as { id?: string | null };
  return r.id ?? null;
}

/** D6: seated + open_table_session (atomic). Requires dining_table_id on the reservation. */
export async function seatReservation(reservationId: string): Promise<{
  reservationId: string;
  diningTableId: string;
  sessionId: string | null;
  customerId: string | null;
}> {
  const { data, error } = await supabase.rpc('seat_reservation', {
    p_reservation_id: reservationId,
  });
  throwIfWriteError('hospitality.reservation_seat', error, reservationId);
  const row = (data ?? {}) as {
    reservation_id?: string | null;
    dining_table_id?: string | null;
    session?: { id?: string | null } | null;
    customer_id?: string | null;
  };
  return {
    reservationId: String(row.reservation_id ?? reservationId),
    diningTableId: String(row.dining_table_id ?? ''),
    sessionId: row.session?.id ? String(row.session.id) : null,
    customerId: row.customer_id ? String(row.customer_id) : null,
  };
}

export async function deleteReservation(reservationId: string): Promise<void> {
  const { error } = await supabase.rpc('delete_reservation', {
    p_reservation_id: reservationId,
  });
  throwIfWriteError('hospitality.reservation', error, reservationId);
}

// ============================================================
// Table booking settings (portal online reservations)
// ============================================================

export type TableBookingSettings = {
  tableBookingEnabled: boolean;
  tableBookingHorizonDays: number;
  tableBookingSlotMinutes: number;
  tableBookingMinLeadHours: number;
  tableBookingCancelHours: number;
  tableBookingDefaultDuration: number;
  tableBookingOpenTime: string;
  tableBookingCloseTime: string;
  tableBookingMinPartySize: number;
  tableBookingDefaultPartySize: number;
  tableBookingMaxPartySize: number | null;
  tableBookingAutoConfirm: boolean;
  tableBookingRequirePhone: boolean;
  tableBookingRequireNotes: boolean;
  tableBookingPaymentMode: 'none' | 'deposit' | 'full';
  tableBookingDepositAmount: number | null;
  tableBookingDepositPercent: number | null;
  tableBookingUnpaidExpireMinutes: number;
};

type RemoteTableBookingRow = {
  table_booking_enabled: boolean | null;
  table_booking_horizon_days: number | null;
  table_booking_slot_minutes: number | null;
  table_booking_min_lead_hours: number | null;
  table_booking_cancel_hours: number | null;
  table_booking_default_duration: number | null;
  table_booking_open_time: string | null;
  table_booking_close_time: string | null;
  table_booking_min_party_size: number | null;
  table_booking_default_party_size: number | null;
  table_booking_max_party_size: number | null;
  table_booking_auto_confirm: boolean | null;
  table_booking_require_phone: boolean | null;
  table_booking_require_notes: boolean | null;
  table_booking_payment_mode: string | null;
  table_booking_deposit_amount: number | null;
  table_booking_deposit_percent: number | null;
  table_booking_unpaid_expire_minutes: number | null;
};

export async function getTableBookingSettings(
  branchId: string,
): Promise<TableBookingSettings | null> {
  const { data, error } = await supabase
    .from('public_store_profiles')
    .select(
      'table_booking_enabled, table_booking_horizon_days, table_booking_slot_minutes, table_booking_min_lead_hours, table_booking_cancel_hours, table_booking_default_duration, table_booking_open_time, table_booking_close_time, table_booking_min_party_size, table_booking_default_party_size, table_booking_max_party_size, table_booking_auto_confirm, table_booking_require_phone, table_booking_require_notes, table_booking_payment_mode, table_booking_deposit_amount, table_booking_deposit_percent, table_booking_unpaid_expire_minutes',
    )
    .eq('branch_id', branchId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) return null;

  const row = data as RemoteTableBookingRow;
  const paymentMode =
    row.table_booking_payment_mode === 'deposit' || row.table_booking_payment_mode === 'full'
      ? row.table_booking_payment_mode
      : 'none';
  return {
    tableBookingEnabled: row.table_booking_enabled === true,
    tableBookingHorizonDays: Number(row.table_booking_horizon_days ?? 14),
    tableBookingSlotMinutes: Number(row.table_booking_slot_minutes ?? 30),
    tableBookingMinLeadHours: Number(row.table_booking_min_lead_hours ?? 2),
    tableBookingCancelHours: Number(row.table_booking_cancel_hours ?? 2),
    tableBookingDefaultDuration: Number(row.table_booking_default_duration ?? 90),
    tableBookingOpenTime: String(row.table_booking_open_time ?? '11:00').slice(0, 5),
    tableBookingCloseTime: String(row.table_booking_close_time ?? '23:00').slice(0, 5),
    tableBookingMinPartySize: Number(row.table_booking_min_party_size ?? 1),
    tableBookingDefaultPartySize: Number(row.table_booking_default_party_size ?? 2),
    tableBookingMaxPartySize:
      row.table_booking_max_party_size != null ? Number(row.table_booking_max_party_size) : null,
    tableBookingAutoConfirm: row.table_booking_auto_confirm !== false,
    tableBookingRequirePhone: row.table_booking_require_phone !== false,
    tableBookingRequireNotes: row.table_booking_require_notes === true,
    tableBookingPaymentMode: paymentMode,
    tableBookingDepositAmount:
      row.table_booking_deposit_amount != null ? Number(row.table_booking_deposit_amount) : null,
    tableBookingDepositPercent:
      row.table_booking_deposit_percent != null ? Number(row.table_booking_deposit_percent) : null,
    tableBookingUnpaidExpireMinutes: Number(row.table_booking_unpaid_expire_minutes ?? 30),
  };
}

export async function updateTableBookingSettings(
  branchId: string,
  patch: Partial<TableBookingSettings> & { clearMaxPartySize?: boolean },
): Promise<void> {
  if (Object.keys(patch).length === 0) return;

  const { error } = await supabase.rpc('upsert_table_booking_settings', {
    p_branch_id: branchId,
    p_table_booking_enabled: patch.tableBookingEnabled ?? null,
    p_table_booking_horizon_days: patch.tableBookingHorizonDays ?? null,
    p_table_booking_slot_minutes: patch.tableBookingSlotMinutes ?? null,
    p_table_booking_min_lead_hours: patch.tableBookingMinLeadHours ?? null,
    p_table_booking_cancel_hours: patch.tableBookingCancelHours ?? null,
    p_table_booking_default_duration: patch.tableBookingDefaultDuration ?? null,
    p_table_booking_open_time: patch.tableBookingOpenTime ?? null,
    p_table_booking_close_time: patch.tableBookingCloseTime ?? null,
    p_table_booking_min_party_size: patch.tableBookingMinPartySize ?? null,
    p_table_booking_default_party_size: patch.tableBookingDefaultPartySize ?? null,
    p_table_booking_max_party_size: patch.tableBookingMaxPartySize ?? null,
    p_table_booking_auto_confirm: patch.tableBookingAutoConfirm ?? null,
    p_table_booking_require_phone: patch.tableBookingRequirePhone ?? null,
    p_table_booking_require_notes: patch.tableBookingRequireNotes ?? null,
    p_table_booking_payment_mode: patch.tableBookingPaymentMode ?? null,
    p_table_booking_deposit_amount: patch.tableBookingDepositAmount ?? null,
    p_table_booking_deposit_percent: patch.tableBookingDepositPercent ?? null,
    p_table_booking_unpaid_expire_minutes: patch.tableBookingUnpaidExpireMinutes ?? null,
    p_clear_max_party_size: patch.clearMaxPartySize === true,
  });

  if (error) throw new Error(error.message);
}

// ============================================================
// Tableside settings (pedido desde la mesa / QR)
// ============================================================

export type TablesideMode = 'signals' | 'draft' | 'auto';

export type TablesideSettings = {
  tablesideEnabled: boolean;
  tablesideMode: TablesideMode;
  tablesideGuestItemNotes: boolean;
  tablesideDraftIdleMinutes: number;
  tablesideLookingIdleMinutes: number;
  autoFreeTableOnPay: boolean;
  tablesideRequirePhone: boolean;
  /** true = tableside_hours null (inherit business_hours). false = always_open override. */
  respectStoreHours: boolean;
  notifyCustomerReadyPickup: boolean;
  notifyGuestReadyDineIn: boolean;
  tablesidePayAtTableEnabled: boolean;
  tablesideTipEnabled: boolean;
  tablesideTipSuggestedPercents: number[];
  tablesideFeedbackEnabled: boolean;
  tablesideSplitEnabled: boolean;
};

type RemoteTablesideRow = {
  tableside_enabled: boolean | null;
  tableside_mode: string | null;
  tableside_guest_item_notes: boolean | null;
  tableside_draft_idle_minutes: number | null;
  tableside_looking_idle_minutes: number | null;
  auto_free_table_on_pay: boolean | null;
  tableside_require_phone: boolean | null;
  tableside_hours: unknown;
  notify_customer_ready_pickup: boolean | null;
  notify_guest_ready_dine_in: boolean | null;
  tableside_pay_at_table_enabled: boolean | null;
  tableside_tip_enabled: boolean | null;
  tableside_tip_suggested_percents: number[] | null;
  tableside_feedback_enabled: boolean | null;
  tableside_split_enabled: boolean | null;
};

function mapTablesideMode(raw: string | null | undefined): TablesideMode {
  if (raw === 'signals' || raw === 'auto') return raw;
  return 'draft';
}

function mapRespectStoreHours(hours: unknown): boolean {
  if (hours == null) return true;
  if (
    typeof hours === 'object' &&
    !Array.isArray(hours) &&
    (hours as { always_open?: unknown }).always_open === true
  ) {
    return false;
  }
  // Custom day/shifts override still counts as not "simple inherit".
  return false;
}

function mapTipPercents(raw: number[] | null | undefined): number[] {
  if (!Array.isArray(raw) || raw.length === 0) return [10, 15, 20];
  const cleaned = raw
    .map((n) => Math.round(Number(n)))
    .filter((n) => Number.isFinite(n) && n >= 1 && n <= 100)
    .slice(0, 6);
  return cleaned.length > 0 ? cleaned : [10, 15, 20];
}

export async function getTablesideSettings(
  branchId: string,
): Promise<TablesideSettings | null> {
  const { data, error } = await supabase
    .from('public_store_profiles')
    .select(
      'tableside_enabled, tableside_mode, tableside_guest_item_notes, tableside_draft_idle_minutes, tableside_looking_idle_minutes, auto_free_table_on_pay, tableside_require_phone, tableside_hours, notify_customer_ready_pickup, notify_guest_ready_dine_in, tableside_pay_at_table_enabled, tableside_tip_enabled, tableside_tip_suggested_percents, tableside_feedback_enabled, tableside_split_enabled',
    )
    .eq('branch_id', branchId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) return null;

  const row = data as RemoteTablesideRow;
  return {
    tablesideEnabled: row.tableside_enabled === true,
    tablesideMode: mapTablesideMode(row.tableside_mode),
    tablesideGuestItemNotes: row.tableside_guest_item_notes === true,
    tablesideDraftIdleMinutes: Number(row.tableside_draft_idle_minutes ?? 30),
    tablesideLookingIdleMinutes: Number(row.tableside_looking_idle_minutes ?? 15),
    autoFreeTableOnPay: row.auto_free_table_on_pay === true,
    tablesideRequirePhone: row.tableside_require_phone === true,
    respectStoreHours: mapRespectStoreHours(row.tableside_hours),
    notifyCustomerReadyPickup: row.notify_customer_ready_pickup === true,
    notifyGuestReadyDineIn: row.notify_guest_ready_dine_in === true,
    tablesidePayAtTableEnabled: row.tableside_pay_at_table_enabled === true,
    tablesideTipEnabled: row.tableside_tip_enabled !== false,
    tablesideTipSuggestedPercents: mapTipPercents(row.tableside_tip_suggested_percents),
    tablesideFeedbackEnabled: row.tableside_feedback_enabled === true,
    tablesideSplitEnabled: row.tableside_split_enabled === true,
  };
}

export async function updateTablesideSettings(
  branchId: string,
  patch: Partial<TablesideSettings>,
): Promise<void> {
  if (Object.keys(patch).length === 0) return;

  const setHours = patch.respectStoreHours !== undefined;
  const { error } = await supabase.rpc('upsert_tableside_settings', {
    p_branch_id: branchId,
    p_tableside_enabled: patch.tablesideEnabled ?? null,
    p_tableside_mode: patch.tablesideMode ?? null,
    p_tableside_guest_item_notes: patch.tablesideGuestItemNotes ?? null,
    p_tableside_draft_idle_minutes: patch.tablesideDraftIdleMinutes ?? null,
    p_tableside_looking_idle_minutes: patch.tablesideLookingIdleMinutes ?? null,
    p_auto_free_table_on_pay: patch.autoFreeTableOnPay ?? null,
    p_tableside_require_phone: patch.tablesideRequirePhone ?? null,
    p_tableside_hours: setHours
      ? patch.respectStoreHours
        ? null
        : { always_open: true }
      : null,
    p_set_tableside_hours: setHours,
    p_notify_customer_ready_pickup: patch.notifyCustomerReadyPickup ?? null,
    p_notify_guest_ready_dine_in: patch.notifyGuestReadyDineIn ?? null,
    p_tableside_pay_at_table_enabled: patch.tablesidePayAtTableEnabled ?? null,
    p_tableside_tip_enabled: patch.tablesideTipEnabled ?? null,
    p_tableside_tip_suggested_percents: patch.tablesideTipSuggestedPercents ?? null,
    p_tableside_feedback_enabled: patch.tablesideFeedbackEnabled ?? null,
    p_tableside_split_enabled: patch.tablesideSplitEnabled ?? null,
  });

  if (error) throw new Error(error.message);
}
